import { useCallback, useEffect, useRef, useState } from "react";
import { debounce } from "throttle-debounce";
import { getSortResult } from "../helpers";
import type { MenuItems } from "../types";

export const useSidebarSearch = (
  items: MenuItems,
  onSelectFirstItem?: (id: string) => void
) => {
  const [isOpenSearchPanel, setIsOpenSearchPanel] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchingMenuItems, setSearchingMenuItems] =
    useState<MenuItems | null>(null);

  useEffect(() => {
    setSearchQuery("");
    setSearchingMenuItems(null);
  }, [items]);

  const debouncedSetSearchingMenuItems = useRef(
    debounce(1000, (results: MenuItems | null) => {
      setSearchingMenuItems(results);
    })
  ).current;

  useEffect(() => {
    if (searchingMenuItems?.length && onSelectFirstItem) {
      onSelectFirstItem(searchingMenuItems[0]?.id);
    }
  }, [searchingMenuItems]);

  const handleOpenSearchPanel = () => {
    setIsOpenSearchPanel((prev) => !prev);
  };

  const handleChangeSearchValue = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setSearchQuery(value);

      debouncedSetSearchingMenuItems.cancel({ upcomingOnly: true });

      if (!value.trim()) {
        setSearchingMenuItems(null);
        return;
      }

      const allItems: MenuItems = [];

      const collectItems = (itemsList: MenuItems) => {
        for (const item of itemsList) {
          if (item.children && item.children.length > 0) {
            collectItems(item.children);
          } else {
            allItems.push(item);
          }
        }
      };

      collectItems(items);
      const sorted = [...allItems]
        .filter((item) => getSortResult(item, value) > 0)
        .sort((a, b) => {
          const scoreA = getSortResult(a, value);
          const scoreB = getSortResult(b, value);
          return scoreB - scoreA;
        });

      debouncedSetSearchingMenuItems(sorted.slice(0, 10));
    },
    [items, debouncedSetSearchingMenuItems]
  );

  const clearSearchInput = useCallback(() => {
    setSearchQuery("");
    setSearchingMenuItems(null);
    debouncedSetSearchingMenuItems.cancel();
  }, []);

  return {
    isOpenSearchPanel,
    searchQuery,
    searchingMenuItems,
    setSearchingMenuItems,
    setIsOpenSearchPanel,
    clearSearchInput,
    handleOpenSearchPanel,
    handleChangeSearchValue,
  };
};
